import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'
import { CButton } from '@coreui/react-pro'
import {
  claimAutoReload,
  getVersionState,
  startVersionPolling,
  subscribeVersionState,
} from '../utils/versionCheck'

export const CRITICAL_RELOAD_SECONDS = 30

// Mounted inside every BrowserRouter in App.js. When a newer build is deployed it:
//  - shows a banner offering a reload;
//  - applies the update on the next route change with a full page load. The user is leaving
//    the screen anyway, so nothing typed is lost;
//  - for a build marked critical, reloads by itself after a short countdown.
// Automatic reloads go through claimAutoReload, so a cached /version.json can never cause a
// reload loop; the "Reload now" button always works.
const VersionWatcher = () => {
  const { updateAvailable, critical, version } = useSyncExternalStore(
    subscribeVersionState,
    getVersionState,
  )
  const location = useLocation()
  const firstLocation = useRef(true)
  const [dismissed, setDismissed] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(CRITICAL_RELOAD_SECONDS)
  // Set when the guard refused an automatic reload: fall back to the ordinary banner.
  const [autoBlocked, setAutoBlocked] = useState(false)
  const forced = critical && !autoBlocked

  useEffect(() => {
    startVersionPolling()
  }, [])

  // Auto-apply on navigation. The URL has already changed, so reload() lands on the new page.
  useEffect(() => {
    if (firstLocation.current) {
      firstLocation.current = false
      return
    }
    if (!updateAvailable) return
    if (claimAutoReload(version)) window.location.reload()
    else setAutoBlocked(true)
    // Only route changes should trigger this, not the update flag flipping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (!updateAvailable || !forced) return undefined
    const timer = setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [updateAvailable, forced])

  useEffect(() => {
    if (!forced || secondsLeft > 0) return
    if (claimAutoReload(version)) window.location.reload()
    else setAutoBlocked(true)
  }, [forced, secondsLeft, version])

  if (!updateAvailable || (dismissed && !forced)) return null

  return (
    <div
      role="status"
      className="position-fixed bottom-0 start-50 translate-middle-x mb-3 px-3 py-2 rounded shadow bg-body border d-flex align-items-center flex-wrap gap-2"
      style={{ zIndex: 2000, maxWidth: 'calc(100vw - 32px)' }}
    >
      <span className="me-2">
        {forced
          ? `An important update is available. Reloading in ${secondsLeft}s…`
          : 'A new version of Vamos is available.'}
      </span>
      <CButton color="primary" size="sm" onClick={() => window.location.reload()}>
        Reload now
      </CButton>
      {!forced && (
        <CButton color="secondary" variant="ghost" size="sm" onClick={() => setDismissed(true)}>
          Later
        </CButton>
      )}
    </div>
  )
}

export default VersionWatcher
